/**
 * Typed hooks for the loyalty preview store
 *
 * These hooks provide type-safe access to the store's selectors and actions.
 */
import { useSelect, useDispatch } from '@wordpress/data';
import type { PreviewState, LoyaltyPreviewStoreConfig } from './loyalty-preview-store';
import { LOYALTY_PREVIEW_STORE_NAME } from './loyalty-preview-store';

// Type the store selector function
type SelectFunction = <T>(
	selector: (select: {
		(storeName: typeof LOYALTY_PREVIEW_STORE_NAME): LoyaltyPreviewStoreConfig['selectors'];
	}) => T
) => T;

/**
 * WordPress data store selector functions (bound to state)
 */
interface BoundLoyaltyPreviewSelectors {
	getPreviewState(): PreviewState;
}

/**
 * Hook to get the current preview state
 *
 * @example
 * ```tsx
 * const previewState = useLoyaltyPreviewState();
 * // previewState is properly typed as PreviewState
 * ```
 */
export function useLoyaltyPreviewState(): PreviewState {
	return useSelect(
		(select) => {
			// WordPress data returns bound selector functions
			const { getPreviewState } = select(LOYALTY_PREVIEW_STORE_NAME) as BoundLoyaltyPreviewSelectors;
			return getPreviewState();
		},
		[]
	);
}

/**
 * Hook to get the preview store actions
 *
 * @example
 * ```tsx
 * const { setPreviewState } = useLoyaltyPreviewActions();
 * setPreviewState('enrolled'); // Properly typed
 * ```
 */
export function useLoyaltyPreviewActions(): LoyaltyPreviewStoreConfig['actions'] {
	return useDispatch(LOYALTY_PREVIEW_STORE_NAME) as LoyaltyPreviewStoreConfig['actions'];
}
